<ng-container *transloco="let t; prefix: 'match-series-modal'">
  <div class="modal-header">
    <h4 class="modal-title" id="modal-basic-title">
      <span class="match-title-prefix">{{ t('title-prefix') }}</span> {{ series().name }}
    </h4>
    <button type="button" class="btn-close" [attr.aria-label]="t('close')" (click)="close()"></button>
  </div>

  <div class="modal-body scrollable-modal d-flex flex-column gap-0">

    @let match = matchInfo();

    <div class="match-subheader d-flex align-items-center flex-wrap gap-3 pt-1">
      <div class="match-description flex-fill">
        <p class="mb-1">{{ t('description') }}</p>
        <p class="mb-0 text-muted small">{{ t('dont-match-hint') }}</p>
      </div>
      <div class="in-kavita-chip d-flex flex-column flex-shrink-0 gap-2">
        <div class="d-flex align-items-center gap-2">
          <app-image [imageUrl]="coverImageUrl()" width="32px" height="46px" />
          <div class="d-flex flex-column gap-0">
            <div class="in-kavita-label text-uppercase">{{ t('in-kavita') }}</div>
            <div class="in-kavita-name fw-bold">{{ series().name }}</div>
            @if (peopleHint(); as hint) {
              <div class="in-kavita-meta text-muted small">
                {{hint}}
              </div>
            }
            <div class="in-kavita-meta text-muted small">
              @if (kavitaVolumeCount() > 0) {
                {{ t('volume-count', {num: kavitaVolumeCount()}) }}
              }
              @if (kavitaChapterCount() > 0) {
                {{ t('chapter-count', {num: kavitaChapterCount()}) }}
              }
              @if (kavitaSpecialCount() > 0) {
                {{ t('special-count', {num: kavitaSpecialCount()}) }}
              }
              <app-series-format [format]="series().format" [useTitle]="false" />
              @if (match) {
                {{match.libraryType | libraryType}}
              }
            </div>
          </div>
        </div>

        @if (match && match.hasMatch) {
          <div class="setting-section-break m-0"></div>
          <div class="matched-to text-muted d-flex align-items-center gap-2">
            <span>{{t('matched-to')}}</span>

            @if (match.isLegacy) {
              <app-scrobble-provider-tag-badge [provider]="ScrobbleProvider.AniList" [id]="match.aniListId" />
            } @else {
              @switch (match.metadataProvider) {
                @case (MetadataProvider.Mangabaka) {
                  <app-scrobble-provider-tag-badge [provider]="ScrobbleProvider.MangaBaka" [id]="match.mangaBakaId" />
                }
                @case (MetadataProvider.ComicBookRoundup) {
                  <app-scrobble-provider-tag-badge [provider]="ScrobbleProvider.Cbr" [id]="match.cbrId" />
                }
                @case (MetadataProvider.Hardcover) {
                  <app-scrobble-provider-tag-badge [provider]="ScrobbleProvider.Hardcover" [id]="match.hardcoverId" [isStandAlone]="match.isStandAlone" />
                }
              }
            }
          </div>
        }
      </div>
    </div>

    @if (match) {
      <div class="provider-card d-flex align-items-center gap-3 p-3 rounded-2 border">

        <div class="provider-logo flex-shrink-0 rounded-2 d-flex align-items-center justify-content-center phone-hidden">
          <i class="fas fa-database" aria-hidden="true"></i>
        </div>

        <div class="flex-grow-1 min-w-0" [formGroup]="formGroup">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="fw-semibold small text-truncate">
              {{t('searching-against', {provider: activeProvider()! | metadataProviderTitle })}}
            </span>
          </div>

          <div class="small text-muted mb-1">
            @if (isProviderSwitched()) {
              {{t('provider-switch-help', {provider: activeProvider()! | metadataProviderTitle })}}
            } @else {
              {{t('change-provider-help')}}
            }
          </div>

          <select class="form-select form-select-sm w-auto" formControlName="provider"
                  [attr.aria-label]="t('change-provider-help')">
            @for (provider of allMetadataProviders; track provider) {
              <option [ngValue]="provider">{{ provider | metadataProviderTitle }}</option>
            }
          </select>
        </div>

        @if (activeProvider() === MetadataProvider.Hardcover) {
          <form [formGroup]="formGroup">
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" formControlName="isStandAlone"
                     id="is-standalone" role="switch" />
              <label class="form-check-label" for="is-standalone">
                {{ t('is-standalone-label') }}
                <i class="fa fa-info-circle" [ngbTooltip]="t('is-standalone-tooltip')"></i>
              </label>
            </div>
          </form>
        }

      </div>
    }

    <!-- Query bar -->
    <div class="py-3" [formGroup]="formGroup">
      <div class="d-flex align-items-stretch gap-2">
        <div class="d-flex align-items-center position-relative flex-fill">
          <i class="fa-solid fa-magnifying-glass query-icon" aria-hidden="true"></i>
          <label for="search-query" class="visually-hidden">{{t('search')}}</label>
          <input class="form-control query-input"
                 id="search-query"
                 formControlName="query"
                 (keydown.enter)="search()" />
          @if (formGroup.get('query')?.value) {
            <button type="button" class="query-clear btn-close btn-close-white btn-sm"
                    [attr.aria-label]="t('clear')"
                    (click)="clearQuery()"></button>
          }
        </div>
        <button type="button" class="btn btn-primary"
                (click)="search()"
                [disabled]="isLoading() || formGroup.get('dontMatch')?.value">
          @if (isLoading()) {
            <span class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">{{ t('searching-alt') }}</span>
            </span>
          } @else {
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <span class="phone-hidden">{{ t('search') }}</span>
          }
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
        <div class="provider-hints d-flex align-items-center flex-wrap gap-1">
          <span class="text-muted">{{ t('try') }}</span>
          <code class="provider-hint">anilist:159441</code>
          <code class="provider-hint">mal:20593</code>
          <code class="provider-hint">mangabaka:1331</code>
          <code class="provider-hint">hardcover:flatland</code>
        </div>
        <div class="form-check form-switch mb-0">
          <input type="checkbox" class="form-check-input" formControlName="dontMatch"
                 id="dont-match" role="switch" />
          <label class="form-check-label" for="dont-match"
                 [ngbTooltip]="t('dont-match-tooltip')">
            {{ t('dont-match-label') }}
          </label>
        </div>
      </div>
    </div>

    <!-- Results count bar -->
    @if (bodyState() === 'results') {
      <div class="match-count-bar">
        {{ t('match-count', { count: matches().length }) }}
      </div>
    }

    <!-- Body -->
    <div class="match-body">
      @switch (bodyState()) {
        @case ('loading') {
          <div class="d-flex justify-content-center align-items-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('loading-alt') }}</span>
            </div>
          </div>
        }
        @case ('empty') {
          <app-empty-state [titleKey]="'empty-title'" [descriptionKey]="'empty-description'"
                           [i18nPrefix]="'match-series-modal'">
            <i icon class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
          </app-empty-state>
        }
        @case ('dont-match') {
          <app-empty-state [titleKey]="'dont-match-active-title'"
                           [descriptionKey]="'dont-match-active-description'"
                           [i18nPrefix]="'match-series-modal'">
            <i icon class="fa-solid fa-ban" aria-hidden="true"></i>
          </app-empty-state>
        }
        @case ('no-results') {
          <app-empty-state [isError]="true"
                           [titleKey]="'no-results-title'"
                           [descriptionKey]="'no-results-description'"
                           [descriptionParams]="{ query: lastQuery() ?? series().name }"
                           [i18nPrefix]="'match-series-modal'">
            <i icon class="fa-solid fa-magnifying-glass-minus" aria-hidden="true"></i>
          </app-empty-state>
        }
        @case ('results') {
          @for (item of matches(); track matchKey(item)) {
            <app-match-series-result-item
              [item]="item"
              [selectedEditionId]="selectedEditionId()"
              [isSelected]="!!selectedItem() && matchKey(item) === matchKey(selectedItem()!)"
              [showSynonyms]="true"
              [query]="lastQuery()"
              (selected)="selectRow($event)"
              (editionSelected)="selectEdition($event.item, $event.edition)"
            />
          }
        }
      }
    </div>

  </div>

  <div class="modal-footer">

    <div class="d-flex w-100 align-items-center gap-2">
      <a class="btn btn-link phone-hidden" rel="noreferrer noopener" target="_blank" [href]="mangaBakaSearchUrl()">
        MangaBaka
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </a>
      <a class="btn btn-link phone-hidden" rel="noreferrer noopener" target="_blank" href="https://anilist.co/">
        AniList
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </a>
      <a class="btn btn-link phone-hidden" rel="noreferrer noopener" target="_blank" [href]="hardcoverSearchUrl()">
        Hardcover
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </a>
      <a class="btn btn-link phone-hidden" rel="noreferrer noopener" target="_blank" [href]="cbrSearchUrl()">
        Comic Book Roundup
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </a>

      <div class="ms-auto d-flex gap-2">
        <button type="button" class="btn btn-secondary" (click)="close()">{{ t('close') }}</button>
        @if (isDontMatch()) {
          <button type="button" class="btn btn-primary" [disabled]="!canSaveDontMatch()" (click)="saveDontMatch()">
            {{ t('save') }}
          </button>
        } @else {
          <button type="button" class="btn btn-primary" [disabled]="!selectedItem()" (click)="applyMatch()">
            {{ t('apply-match') }}
          </button>
        }
      </div>
    </div>
  </div>
</ng-container>
